(() => {
  const readJSON = (node) => {
    try { return JSON.parse(node?.textContent || '[]'); } catch { return []; }
  };
  if (!customElements.get('vallario-approved-gallery')) {
    customElements.define('vallario-approved-gallery', class extends HTMLElement {
      connectedCallback() {
        this.refreshGallery();
        this.onClick = (event) => {
          const choice = event.target.closest('[data-gallery-choice]');
          if (choice && this.contains(choice)) this.showImage(Number(choice.dataset.galleryIndex));
        };
        this.onScroll = (event) => {
          if (event.target !== this.track) return;
          cancelAnimationFrame(this.scrollFrame);
          this.scrollFrame = requestAnimationFrame(() => {
            const width = this.track?.clientWidth || 0;
            if (width > 0) this.updateSelection(Math.round(this.track.scrollLeft / width));
          });
        };
        this.onKeydown = (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          const choice = event.target.closest('[data-gallery-choice]');
          if (!choice && event.target !== this.track) return;
          const current = choice ? Number(choice.dataset.galleryIndex) : this.activeIndex;
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : current + (event.key === 'ArrowRight' ? 1 : -1);
          event.preventDefault();
          this.showImage(next);
          if (choice) this.choices[this.activeIndex]?.focus();
        };
        this.addEventListener('click', this.onClick);
        this.addEventListener('scroll', this.onScroll, true);
        this.addEventListener('keydown', this.onKeydown);
        if (typeof subscribe === 'function') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (String(data.sectionId) !== this.dataset.section) return;
            const source = data.html.querySelector('vallario-approved-gallery');
            if (!source) return;
            this.replaceChildren(...[...source.childNodes].map(node => node.cloneNode(true)));
            this.refreshGallery();
          });
        }
      }
      refreshGallery() {
        this.track = this.querySelector('[data-gallery-track]');
        this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
        this.choices = [...this.querySelectorAll('[data-gallery-choice]')];
        this.activeIndex = 0;
        if (this.track) this.track.scrollLeft = 0;
        this.updateSelection(0);
      }
      showImage(index) {
        if (!this.track || !this.slides.length || !Number.isFinite(index)) return;
        const next = Math.max(0, Math.min(index, this.slides.length - 1));
        this.updateSelection(next);
        this.track.scrollTo({ left: next * this.track.clientWidth, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }
      updateSelection(index) {
        if (!this.slides.length) return;
        const nextIndex = Number.isFinite(index) ? Math.round(index) : 0;
        this.activeIndex = Math.max(0, Math.min(nextIndex, this.slides.length - 1));
        this.choices.forEach((choice, position) => choice.setAttribute('aria-pressed', String(position === this.activeIndex)));
        const status = this.querySelector('[data-gallery-status]');
        if (status) status.textContent = `Photo ${this.activeIndex + 1} of ${this.slides.length}`;
      }
      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('scroll', this.onScroll, true);
        this.removeEventListener('keydown', this.onKeydown);
        cancelAnimationFrame(this.scrollFrame);
        this.unsubscribe?.();
      }
    });
  }

  if (!customElements.get('vallario-approved-controls')) {
    customElements.define('vallario-approved-controls', class extends HTMLElement {
      connectedCallback() {
        this.root = this.closest('product-info');
        if (!this.root?.hasAttribute('data-vallario-approved')) return;
        this.onApprovedSubmit = (event) => {
          const form = event.target;
          if (!form?.matches?.('form[data-type="add-to-cart-form"]')) return;
          const submittedId = form.querySelector('input[name="id"]')?.value;
          const allowedVariants = readJSON(this.querySelector('[data-allowed-variants]'));
          const submitButton = form.querySelector('[type="submit"]');
          const quantity = Number(this.root.querySelector('input[name="quantity"]')?.value ?? 1);
          if (!submittedId || !allowedVariants.some(variant => variant.available && String(variant.id) === String(submittedId)) ||
              submitButton?.disabled || submitButton?.getAttribute('aria-disabled') === 'true' ||
              !Number.isInteger(quantity) || quantity < 1 || !form.checkValidity()) {
            event.preventDefault();
            event.stopImmediatePropagation();
            form.reportValidity();
          }
        };
        this.root.addEventListener('submit', this.onApprovedSubmit, true);
        const current = readJSON(this.root?.querySelector('[data-selected-variant]'));
        const allowed = readJSON(this.querySelector('[data-allowed-variants]'));
        this.updateAvailability(current);
        if (allowed.length && current?.id && !allowed.some(item => String(item.id) === String(current.id))) {
          const fallback = allowed.find(item => item.available) || allowed[0];
          const url = new URL(window.location.href);
          url.searchParams.set('variant', fallback.id);
          window.location.replace(url.href);
          return;
        }
        this.refresh = (variant) => {
          if (!variant?.id) variant = readJSON(this.root?.querySelector('[data-selected-variant]'));
          this.updateAvailability(variant);
          const button = this.root?.querySelector('.product-form__submit');
          if (variant?.available && button && !button.disabled && button.getAttribute('aria-disabled') !== 'true') {
            const span = button.querySelector('span');
            if (span) {
              const total = this.root.querySelector('vallario-purchase-panel')?.dataset.purchaseTotal || new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {style:'currency',currency:this.root.querySelector('variant-selects')?.dataset.currencyCode || 'AUD'}).format((variant.price || 0) * Number(this.root.querySelector('input[name="quantity"]')?.value || 1) / 100);
              span.textContent = `${this.root.dataset.ctaLabel || 'Add to bag'}${this.root.dataset.showCtaPrice === 'true' ? ` · ${total}` : ''}`;
            }
          }
          this.root?.querySelectorAll('[data-format-link]').forEach((link) => {
            const variants = readJSON(link.querySelector('[data-format-variants]'));
            const target = variants.find((item) => item.size === variant?.option1) || variants.find(item => item.available) || variants[0];
            if (target) link.href = `${link.dataset.productUrl}?variant=${target.id}`;
          });
          this.root?.dispatchEvent(new CustomEvent('vallario:purchase-update', { bubbles:true }));
        };
        if (typeof subscribe === 'function') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (String(data.sectionId) === this.root?.dataset.section) this.refresh(data.variant);
          });
        }
        this.root?.addEventListener('input', this.refresh);
        this.root?.addEventListener('change', this.refresh);
        this.refresh();
      }
      updateAvailability(variant) {
        const allowed = readJSON(this.querySelector('[data-allowed-variants]'));
        const listed = allowed.find(item => String(item.id) === String(variant?.id));
        const blocked = !listed || !listed.available || variant?.available === false;
        const form = this.root?.querySelector('form[data-type="add-to-cart-form"]');
        const input = form?.querySelector('input[name="id"]');
        const button = form?.querySelector('[type="submit"]');
        if (input) input.disabled = blocked;
        if (blocked && button) {
          button.disabled = true;
          if (!listed) {
            const label = button.querySelector('span');
            if (label) label.textContent = window.variantStrings?.unavailable || 'Unavailable';
          }
        }
        this.root?.querySelectorAll('.shopify-payment-button, shopify-accelerated-checkout').forEach(wallet => {
          if (blocked && !wallet.hidden) {
            wallet.dataset.vpaGuardHidden = 'true';
            wallet.hidden = true;
          } else if (!blocked && wallet.dataset.vpaGuardHidden === 'true') {
            delete wallet.dataset.vpaGuardHidden;
            wallet.hidden = false;
          }
        });
      }
      disconnectedCallback() {
        this.unsubscribe?.();
        this.root?.removeEventListener('submit', this.onApprovedSubmit, true);
        this.root?.removeEventListener('input', this.refresh);
        this.root?.removeEventListener('change', this.refresh);
      }
    });
  }

  if (!customElements.get('vallario-phone-media')) {
    customElements.define('vallario-phone-media', class extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        this.gif = this.querySelector('[data-gif-src]');
        this.button = this.querySelector('[data-media-toggle]');
        this.poster = this.querySelector('[data-static-poster]');
        this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (!this.video && !this.gif) return;
        this.paused = this.motion.matches;
        this.apply = () => {
          if (this.button) {
            this.button.textContent = this.paused ? 'Play' : 'Pause';
            this.button.setAttribute('aria-pressed', String(!this.paused));
          }
          if (this.video) {
            this.video.hidden = this.paused && Boolean(this.poster);
            if (this.paused) this.video.pause();
            else this.video.play().catch(() => { this.paused = true; this.apply(); });
          }
          if (this.gif) {
            this.gif.hidden = this.paused;
            if (this.paused) this.gif.removeAttribute('src');
            else this.gif.src = this.gif.dataset.gifSrc;
          }
          if (this.poster) this.poster.hidden = !this.paused;
          this.toggleAttribute('data-paused', this.paused);
        };
        this.onClick = () => { this.paused = !this.paused; this.apply(); };
        this.onMotion = () => { this.paused = this.motion.matches; this.apply(); };
        this.button?.addEventListener('click', this.onClick);
        this.motion.addEventListener('change', this.onMotion);
        this.apply();
        if (this.video) {
          this.onVisibility = () => { if (document.hidden) this.video.pause(); else if (!this.paused) this.video.play().catch(() => {}); };
          document.addEventListener('visibilitychange', this.onVisibility);
        }
      }
      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onClick);
        this.motion?.removeEventListener('change', this.onMotion);
        if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
        this.video?.pause();
      }
    });
  }
})();
